import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';

import type { BranchWorkingHour } from 'src/api/branch_api_data';

// ----------------------------------------------------------------------

// dayOfWeek: 1=Pazartesi .. 7=Pazar (ISO)
const DAY_NAMES: Record<number, string> = {
  1: 'Pazartesi',
  2: 'Salı',
  3: 'Çarşamba',
  4: 'Perşembe',
  5: 'Cuma',
  6: 'Cumartesi',
  7: 'Pazar',
};

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 7];

// Varsayılan hafta: Pzt-Cuma 09:00-18:00 açık, Cmt/Paz kapalı.
export function getDefaultWorkingHours(): BranchWorkingHour[] {
  return DAY_ORDER.map((dayOfWeek) => {
    const isWeekend = dayOfWeek === 6 || dayOfWeek === 7;
    return {
      dayOfWeek,
      closed: isWeekend,
      openTime: isWeekend ? null : '09:00',
      closeTime: isWeekend ? null : '18:00',
    };
  });
}

// Eski tekil açılış/kapanış saatinden 7 günlük varsayılan üret (düzenleme geçişi için).
export function workingHoursFromSingle(
  openTime?: string | null,
  closeTime?: string | null
): BranchWorkingHour[] {
  const open = openTime || '09:00';
  const close = closeTime || '18:00';
  return DAY_ORDER.map((dayOfWeek) => {
    const isWeekend = dayOfWeek === 6 || dayOfWeek === 7;
    return {
      dayOfWeek,
      closed: isWeekend,
      openTime: isWeekend ? null : open,
      closeTime: isWeekend ? null : close,
    };
  });
}

// Verilen değeri 7 günlük tam haftaya normalize eder (eksikleri varsayılanla doldurur).
export function normalizeWorkingHours(value?: BranchWorkingHour[] | null): BranchWorkingHour[] {
  if (!value || value.length === 0) {
    return getDefaultWorkingHours();
  }
  const byDay = new Map(value.map((wh) => [wh.dayOfWeek, wh]));
  const defaults = getDefaultWorkingHours();
  return DAY_ORDER.map((dayOfWeek) => byDay.get(dayOfWeek) || defaults[dayOfWeek - 1]);
}

// Açık bir günün açılış == kapanış olması hatalıdır (backend validateBranchHours ile uyumlu: yalnız
// open == close reddedilir). Gece vardiyaları (22:00→06:00 gibi gece yarısını geçen) yasaldır; sözlüksel
// "<" karşılaştırması ("22:00" < "06:00" === false) bunları hatalı biçimde reddediyordu (6841e8f regresyonu).
export function validateWorkingHours(value: BranchWorkingHour[]): boolean {
  return value.every((wh) => {
    if (wh.closed) return true;
    if (!wh.openTime || !wh.closeTime) return false;
    return wh.openTime !== wh.closeTime;
  });
}

type Props = {
  value: BranchWorkingHour[];
  onChange: (v: BranchWorkingHour[]) => void;
  disabled?: boolean;
};

export function WorkingHoursGrid({ value, onChange, disabled = false }: Props) {
  const rows = normalizeWorkingHours(value);

  const updateDay = (dayOfWeek: number, patch: Partial<BranchWorkingHour>) => {
    onChange(rows.map((wh) => (wh.dayOfWeek === dayOfWeek ? { ...wh, ...patch } : wh)));
  };

  const handleToggle = (dayOfWeek: number, open: boolean) => {
    if (open) {
      // Kapalıdan açığa: saat yoksa varsayılan ata.
      const current = rows.find((wh) => wh.dayOfWeek === dayOfWeek);
      updateDay(dayOfWeek, {
        closed: false,
        openTime: current?.openTime || '09:00',
        closeTime: current?.closeTime || '18:00',
      });
    } else {
      updateDay(dayOfWeek, { closed: true });
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Çalışma Saatleri
      </Typography>
      <Stack spacing={1.5}>
        {rows.map((wh) => {
          const open = !wh.closed;
          const invalid = open && !!wh.openTime && !!wh.closeTime && wh.openTime === wh.closeTime;
          return (
            <Stack
              key={wh.dayOfWeek}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Typography sx={{ minWidth: 96 }}>{DAY_NAMES[wh.dayOfWeek]}</Typography>
              <FormControlLabel
                sx={{ minWidth: 120 }}
                control={
                  <Switch
                    checked={open}
                    disabled={disabled}
                    onChange={(e) => handleToggle(wh.dayOfWeek, e.target.checked)}
                  />
                }
                label={open ? 'Açık' : 'Kapalı'}
              />
              <TextField
                label="Açılış"
                type="time"
                size="small"
                value={wh.openTime || ''}
                onChange={(e) => updateDay(wh.dayOfWeek, { openTime: e.target.value })}
                disabled={disabled || !open}
                error={invalid}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 140 }}
              />
              <TextField
                label="Kapanış"
                type="time"
                size="small"
                value={wh.closeTime || ''}
                onChange={(e) => updateDay(wh.dayOfWeek, { closeTime: e.target.value })}
                disabled={disabled || !open}
                error={invalid}
                helperText={invalid ? 'Açılış ve kapanış aynı olamaz' : ''}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 140 }}
              />
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
